import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../shared/SEO';
import { Header } from './Header';
import { Footer } from './Footer';
import { MobileFooterNav } from './MobileFooterNav';
import { MobileHomeHeader } from '../MobileHomeHeader';
import { LocationModal } from '../modals/LocationModal';
import { AnnouncementBar } from './AnnouncementBar';
import { MobileDeliverToPill } from './MobileDeliverToPill';
import { PageHeroBreadcrumb } from './PageHeroBreadcrumb';
import { ChevronRightIcon, PrinterIcon, DownloadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface PolicySection {
  id: string;
  title: string;
  content: React.ReactNode;
}
interface PolicyLayoutProps {
  title: string;
  description: string;
  lastUpdated: string;
  sections: PolicySection[];
  seoTitle: string;
  seoDescription: string;
  canonical: string;
}
export function PolicyLayout({
  title,
  description,
  lastUpdated,
  sections,
  seoTitle,
  seoDescription,
  canonical
}: PolicyLayoutProps) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => document.getElementById(section.id));
      const scrollPosition = window.scrollY + 200;
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 140;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };
  const handlePrint = () => {
    window.print();
  };
  return <HelmetProvider data-id="element-2558" data-mp="21n8jrmq1ihhh">
      <SEO title={seoTitle} description={seoDescription} canonical={canonical} data-id="element-2559" data-mp="21nqol7qrq410" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-2560" data-mp="14y9ca9xa5jqc">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-2561" data-mp="18zuu89oo8o7e">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-2562" data-mp="18wqjsukm4w8a" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-2563" data-mp="l8yv0rhvyqq4">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-2564" data-mp="inregawly5pq">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-2565" data-mp="dpzc0myc717a" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: title
        }]} title={title} description={description} data-id="element-2566" data-mp="4orrr8sno0w7" />
          {/* Last Updated Info */}
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] text-white py-4" data-id="element-2567" data-mp="3h6px892hpktk">
            <div className="container mx-auto px-4" data-id="element-2568" data-mp="3d4ux37wapdhd">
              <p className="text-sm text-blue-200" data-id="element-2569" data-mp="3d3jmao4fn5gr">
                Last Updated: {lastUpdated}
              </p>
            </div>
          </div>
          <div className="container mx-auto px-4 py-8" data-id="element-2570" data-mp="1c2llk6kq3f28">
            <div className="flex flex-col lg:flex-row gap-8" data-id="element-2571" data-mp="1c355fstxodiw">
              {/* Table of Contents - Desktop */}
              <aside className="hidden lg:block lg:w-80 flex-shrink-0" data-id="element-2572" data-mp="1g3fexfgxpm2i">
                <div className="sticky top-32 bg-white rounded-xl shadow-sm p-6 border border-gray-200" data-id="element-2573" data-mp="12c0rmwfzuypn">
                  <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2574" data-mp="17alr2tnf9ed1">
                    Table of Contents
                  </h2>
                  <nav className="space-y-2" data-id="element-2575" data-mp="sgz4251da4p6">
                    {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeSection === section.id ? 'bg-blue-50 text-[#1E2B55] font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2576" data-mp="xbcrnl9sf7ja">
                        {section.title}
                      </button>)}
                  </nav>
                  <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-2577" data-mp="2sv8nf39fnulv">
                    <button onClick={handlePrint} className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" data-id="element-2578" data-mp="2nwyvr2mx3c50">
                      <PrinterIcon className="w-4 h-4" data-id="element-2579" data-mp="3bkyt4leqi0sk" />
                      Print
                    </button>
                  </div>
                </div>
              </aside>
              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-2580" data-mp="3kmg6hw0rbodk">
                <div className="bg-white rounded-xl shadow-sm p-6 pt-12 md:p-8 border border-gray-200" data-id="element-2581" data-mp="3kk2drttjuo96">
                  <div className="prose prose-lg max-w-none" data-id="element-2582" data-mp="3gigrjhv4kxmb">
                    {sections.map((section, index) => <div key={section.id} id={section.id} className="mb-12" data-id="element-2583" data-mp="1m6k0vr4ibvsb" data-mpi={index}>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-2584" data-mp="1m8wp2m0uz2dt">
                          <span className="text-[#1E2B55] mr-3" data-id="element-2585" data-mp="3ldejmuqotjwz">
                            {index + 1}.
                          </span>
                          {section.title}
                        </h2>
                        <div className="text-gray-600 leading-relaxed" data-id="element-2586" data-mp="3vbub4ytmmv70">
                          {section.content}
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Contact CTA */}
                <div className="mt-8 bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-6 md:p-8 text-white" data-id="element-2587" data-mp="d220o89dl693">
                  <h3 className="text-xl font-bold mb-2" data-id="element-2588" data-mp="hwxsp8em3tm7">Have Questions?</h3>
                  <p className="text-blue-100 mb-4" data-id="element-2589" data-mp="mswkz7329vrw">
                    If you have any questions about this policy, please contact
                    us.
                  </p>
                  <a href="/contact-us" className="inline-flex items-center gap-2 bg-white text-[#1E2B55] font-bold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-2590" data-mp="vv5upuqi9l48">
                    Contact Us
                    <ChevronRightIcon className="w-5 h-5" data-id="element-2591" data-mp="27bgz71ogkj65" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-2592" data-mp="22eqjifgkfpl9">
          <Footer data-id="element-2593" data-mp="300ntfogvqmll" />
        </div>
        <MobileFooterNav data-id="element-2594" data-mp="2zzuw1e3t99g4" />
        <div className="h-16 md:hidden" data-id="element-2595" data-mp="3027b9ji6p5ph"></div>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-2596" data-mp="2v233qiz97lbe" />
      </div>
    </HelmetProvider>;
}